import { intersect, polygon as polygonOps } from '../geometry';
import { type Vec2, vec } from '../math';

import { type Vec3 } from './Vec3';

/**
 * Which parts of a segment a solid hides, exactly, for a solid of any shape.
 *
 * ## What this replaces, and why the old rule was not merely approximate
 *
 * Visibility was decided by **classifying faces**: an edge whose adjacent faces all point away
 * from the viewer is hidden. For a convex solid that is exact and beautifully cheap — no depth
 * sort, no edge splitting, right at every zoom. Every primitive in `mesh.ts` is convex, and the
 * rule was chosen knowing that.
 *
 * It fails the moment a solid is not. An L-shaped block hides an edge behind a face that is not
 * adjacent to it, and face classification cannot see that: the edge's own faces both point
 * forward, so it is drawn solid, straight through the arm of the L in front of it. The figure is
 * then wrong in the one way this product exists to prevent — it looks entirely plausible, and
 * only somebody who already knows the answer can tell.
 *
 * There is a second, quieter failure that has nothing to do with convexity. A user's own segment
 * was tested against the solid by its **midpoint**: inside means dashed, outside means solid. A
 * segment running half in and half out is therefore drawn entirely one way, and which way depends
 * on where its middle happens to fall.
 *
 * Both are the same missing piece — nobody was **cutting** anything — so both are answered here.
 *
 * ## The algorithm, and why it is exact
 *
 * Under a parallel projection every point of space that lands on one page point lies on one
 * straight ray, and depth along that ray is affine. So:
 *
 * 1. Cut the segment wherever its *image* crosses the *image* of any face boundary. Between two
 *    consecutive cuts nothing can change: either the whole piece is behind a given face or none
 *    of it is.
 * 2. Take each piece's midpoint, follow the viewing ray forward, and ask which faces it meets.
 *
 * No tolerance decides the answer, no sampling, no z-buffer. The result is right at any zoom,
 * which is the same promise the old rule made and could only keep for convex bodies.
 *
 * ## Why parallel only
 *
 * Under perspective the map is not affine, the ray through a page point is not the same ray for
 * every depth, and step 1's "nothing changes between two cuts" stops being true. The caller keeps
 * the older rule there, and says so — the same line every exact-conic path in this folder draws.
 */

/** A face, as its corners in space. Wound either way; the winding is not consulted. */
export type Face = readonly Vec3[];

/** One piece of the segment, as the parameters along it, and whether the solid covers it. */
export interface Piece {
  readonly from: number;
  readonly to: number;
  readonly hidden: boolean;
}

/**
 * How far off a plane a point may be and still count as lying in it.
 *
 * Generous, and it has to be: an edge of the solid lies *in* two of its own faces, and a face
 * that hid the edges lying in it would dash the entire figure. In world units, where a school
 * figure is a few hundred across.
 */
const ON_PLANE = 1e-6;

/** Two cut parameters closer than this are one cut, found twice. */
const SAME_CUT = 1e-9;

export interface SolidView {
  /** Where a point in space lands on the page. */
  readonly project: (point: Vec3) => Vec2;
  /**
   * The direction the viewer is in, in the same space as the faces.
   *
   * Every point of the ray `p + t·toViewer` projects to the same page point, and larger `t` is
   * nearer the reader. That is the whole of what makes the cutting exact.
   */
  readonly toViewer: Vec3;
}

/**
 * The pieces of `a`→`b` that `faces` covers, and the pieces it does not.
 *
 * Always at least one piece. A segment nothing covers comes back as a single visible piece, which
 * is the common case and costs one point-in-polygon test per face.
 */
export function occludeSegment(
  a: Vec3,
  b: Vec3,
  faces: readonly Face[],
  view: SolidView
): readonly Piece[] {
  const pageA = view.project(a);
  const pageB = view.project(b);

  // A segment seen end-on is a point on the page. There is nothing to cut and nothing to draw.
  if (vec.distance(pageA, pageB) < ON_PLANE) return [{ from: 0, to: 1, hidden: false }];

  const outlines = faces.map((face) => face.map(view.project));
  const cuts = cutsAlong(
    pageA,
    pageB,
    outlines,
    faces.flatMap((face) => piercing(a, b, face))
  );

  const pieces: Piece[] = [];
  for (let index = 0; index + 1 < cuts.length; index += 1) {
    const from = cuts[index] ?? 0;
    const to = cuts[index + 1] ?? 1;
    if (to - from < SAME_CUT) continue;

    const middle = (from + to) / 2;
    pieces.push({ from, to, hidden: covered(lerp3(a, b, middle), faces, outlines, view) });
  }

  if (pieces.length === 0) return [{ from: 0, to: 1, hidden: false }];
  return merge(pieces);
}

/**
 * Where the segment's image crosses any face's image, as parameters along it.
 *
 * The ends are always included, so the list describes a covering of the whole segment. Crossings
 * are found in 2D on purpose: a face hides what is behind it *as drawn*, and the boundary of what
 * it hides is the boundary of its picture.
 */
function cutsAlong(
  a: Vec2,
  b: Vec2,
  outlines: readonly (readonly Vec2[])[],
  crossings: readonly number[] = []
): readonly number[] {
  const along = vec.subtract(b, a);
  const lengthSquared = vec.dot(along, along);

  const found = [0, 1, ...crossings.filter((t) => t > SAME_CUT && t < 1 - SAME_CUT)];
  for (const outline of outlines) {
    for (let index = 0; index < outline.length; index += 1) {
      const from = outline[index];
      const to = outline[(index + 1) % outline.length];
      if (from === undefined || to === undefined) continue;

      const at = intersect.segmentSegment({ a, b }, { a: from, b: to });
      if (at === null) continue;

      const t = vec.dot(vec.subtract(at, a), along) / lengthSquared;
      if (t > SAME_CUT && t < 1 - SAME_CUT) found.push(t);
    }
  }

  const sorted = [...found].sort((first, second) => first - second);
  const kept: number[] = [];
  for (const t of sorted) {
    const last = kept[kept.length - 1];
    if (last === undefined || t - last > SAME_CUT) kept.push(t);
  }
  return kept;
}

/**
 * Where the segment crosses a face's own plane, as a parameter along it.
 *
 * ## Why the image crossings are not enough on their own
 *
 * For a **closed body** they are: a segment entering and leaving a convex block always crosses
 * the outline of some face in the picture, so a cut lands wherever the answer changes. A single
 * face is not a body. A line piercing the middle of one has no image crossing at the pierce —
 * that point is well inside the outline — so nothing was cut there, one midpoint decided the
 * whole stretch, and a line through a plane came out entirely solid or entirely dashed.
 *
 * That is the failure this file's own header describes and fixes for bodies: *"a segment running
 * half in and half out is drawn entirely one way, and which way depends on where its middle
 * happens to fall."* It came back the moment a lone face was handed in, which is what a blackboard
 * plane is.
 *
 * Adding the cut is safe for bodies too. It can only split a piece into two with the same answer,
 * and `merge` puts those back together — so a solid's picture is unchanged.
 *
 * Empty when the segment runs parallel to the plane, or crosses it outside its own length.
 */
function piercing(a: Vec3, b: Vec3, face: Face): readonly number[] {
  const [first, second, third] = face;
  if (first === undefined || second === undefined || third === undefined) return [];

  const edge1 = subtract3(second, first);
  const edge2 = subtract3(third, first);
  const normal = {
    x: edge1.y * edge2.z - edge1.z * edge2.y,
    y: edge1.z * edge2.x - edge1.x * edge2.z,
    z: edge1.x * edge2.y - edge1.y * edge2.x,
  };

  const along = subtract3(b, a);
  const rate = normal.x * along.x + normal.y * along.y + normal.z * along.z;
  if (Math.abs(rate) < SAME_CUT) return [];

  const from = subtract3(a, first);
  const t = -(normal.x * from.x + normal.y * from.y + normal.z * from.z) / rate;
  return t > 0 && t < 1 ? [t] : [];
}

function subtract3(a: Vec3, b: Vec3): Vec3 {
  return { x: a.x - b.x, y: a.y - b.y, z: a.z - b.z };
}

/**
 * Whether any face is between this point and the reader.
 *
 * The point's page position is inside the face's outline **and** the face's plane is met going
 * forward along the viewing ray. Both are needed and neither is enough: a face can be drawn over
 * a point while lying behind it, which is exactly what the far wall of a box does.
 */
function covered(
  point: Vec3,
  faces: readonly Face[],
  outlines: readonly (readonly Vec2[])[],
  view: SolidView
): boolean {
  const page = view.project(point);

  for (let index = 0; index < faces.length; index += 1) {
    const face = faces[index];
    const outline = outlines[index];
    if (face === undefined || outline === undefined || outline.length < 3) continue;
    if (!polygonOps.containsPoint(outline, page)) continue;

    const ahead = distanceToPlane(point, face, view.toViewer);
    if (ahead !== null && ahead > ON_PLANE) return true;
  }
  return false;
}

/**
 * How far forward along the viewing ray this face's plane is, or `null` if the ray misses it.
 *
 * `null` covers the two cases that are not a crossing: the ray runs parallel to the plane, and
 * the face is degenerate. Both mean "this face does not cover the point", which is what the
 * caller does with it.
 */
function distanceToPlane(point: Vec3, face: Face, toViewer: Vec3): number | null {
  const normal = normalOf(face);
  if (normal === null) return null;

  const onto = normal.x * toViewer.x + normal.y * toViewer.y + normal.z * toViewer.z;
  if (Math.abs(onto) < ON_PLANE) return null;

  const anchor = face[0];
  if (anchor === undefined) return null;

  const offset = {
    x: anchor.x - point.x,
    y: anchor.y - point.y,
    z: anchor.z - point.z,
  };
  return (offset.x * normal.x + offset.y * normal.y + offset.z * normal.z) / onto;
}

/**
 * Newell's normal, which is the right one for a face that is not perfectly flat.
 *
 * Three corners give a normal only if they are not collinear, and a quadrilateral face of a
 * hand-built solid can easily have three that nearly are. Newell's sums over every edge, so it
 * degrades gracefully instead of picking a bad triple.
 */
function normalOf(face: Face): Vec3 | null {
  let x = 0;
  let y = 0;
  let z = 0;

  for (let index = 0; index < face.length; index += 1) {
    const current = face[index];
    const next = face[(index + 1) % face.length];
    if (current === undefined || next === undefined) continue;

    x += (current.y - next.y) * (current.z + next.z);
    y += (current.z - next.z) * (current.x + next.x);
    z += (current.x - next.x) * (current.y + next.y);
  }

  const length = Math.hypot(x, y, z);
  return length < ON_PLANE ? null : { x: x / length, y: y / length, z: z / length };
}

/**
 * Whether every vertex lies on the inner side of every face: the fast rule's precondition.
 *
 * Asked once per mesh and worth asking, because the fast rule is not merely cheaper. Classifying
 * faces is one comparison per edge and stays exact at any zoom; cutting is a pass over every face
 * for every edge, which a forty-eight-sided cylinder feels on every frame of a drag. So the exact
 * cutter is spent where it is *needed* rather than everywhere, and every solid the product ships
 * today keeps the path it has always had — which is also why this change moves no existing figure.
 */
export function isConvexMesh(mesh: {
  readonly vertices: readonly Vec3[];
  readonly faces: readonly (readonly number[])[];
}): boolean {
  /*
   * Scaled to the body, not absolute. A tolerance in world units calls a figure three units
   * across convex whatever it is, and a figure three hundred across non-convex for a rounding
   * error — the same mistake in both directions.
   */
  const spread = extentOf(mesh.vertices);
  if (spread === 0) return true;
  const slack = spread * 1e-9;

  for (const ring of mesh.faces) {
    const corners = ring.flatMap((index) => {
      const vertex = mesh.vertices[index];
      return vertex === undefined ? [] : [vertex];
    });
    if (corners.length < 3) continue;

    const normal = normalOf(corners);
    const anchor = corners[0];
    if (normal === null || anchor === undefined) continue;

    /*
     * Which side is "inner" is not assumed from the winding. A mesh whose faces are wound
     * inconsistently — which a hand-built one may well be — would otherwise be called non-convex
     * and pay for the exact cutter it does not need. So both sides are allowed, and what is
     * asked is whether every vertex is on the *same* one.
     */
    let above = false;
    let below = false;
    for (const vertex of mesh.vertices) {
      const distance =
        (vertex.x - anchor.x) * normal.x +
        (vertex.y - anchor.y) * normal.y +
        (vertex.z - anchor.z) * normal.z;
      if (distance > slack) above = true;
      if (distance < -slack) below = true;
      if (above && below) return false;
    }
  }
  return true;
}

/**
 * How large the body is, as the longest side of its bounding box.
 *
 * One pass, not the pair of nested loops it started as. That version compared every vertex with
 * every other — fine for a cube's eight and quadratic for a sphere's several hundred, on a
 * function called once per build of every solid on the canvas. The bounding box gives the same
 * scale for the tolerance and is linear.
 */
function extentOf(vertices: readonly Vec3[]): number {
  let least = { x: Infinity, y: Infinity, z: Infinity };
  let most = { x: -Infinity, y: -Infinity, z: -Infinity };

  for (const vertex of vertices) {
    least = {
      x: Math.min(least.x, vertex.x),
      y: Math.min(least.y, vertex.y),
      z: Math.min(least.z, vertex.z),
    };
    most = {
      x: Math.max(most.x, vertex.x),
      y: Math.max(most.y, vertex.y),
      z: Math.max(most.z, vertex.z),
    };
  }

  const spread = Math.max(most.x - least.x, most.y - least.y, most.z - least.z);
  return Number.isFinite(spread) ? Math.max(spread, 0) : 0;
}

/**
 * The direction the viewer lies in, recovered from the projector itself.
 *
 * Derived rather than imported, because each projection encodes the eye's position differently
 * and this file has no business knowing which one it was handed. For a parallel projection the
 * map to the page is linear, so it is settled by where the three unit axes land: the viewing
 * direction is the one combination that lands on nothing.
 *
 * `null` under perspective, where the map is not linear — there the ray through a page point is a
 * different ray at every depth, and the whole argument for exact cutting collapses. The caller
 * keeps the older rule and says so.
 */
export function viewerDirection(projector: {
  toPlane: (view: Vec3) => Vec2;
  isFrontFacing: (normalInView: Vec3, centroidInView: Vec3) => boolean;
}): Vec3 | null {
  const origin = projector.toPlane({ x: 0, y: 0, z: 0 });
  const axis = (x: number, y: number, z: number): Vec2 =>
    vec.subtract(projector.toPlane({ x, y, z }), origin);

  const ux = axis(1, 0, 0);
  const uy = axis(0, 1, 0);
  const uz = axis(0, 0, 1);

  // Linear only if doubling an axis doubles its image. Perspective fails this, which is the test.
  const twice = axis(2, 0, 0);
  if (vec.distance(twice, vec.scale(ux, 2)) > 1e-9) return null;

  // Solve `dx·ux + dy·uy = -uz` for the direction that projects to nothing, with dz = 1.
  const determinant = ux.x * uy.y - ux.y * uy.x;
  if (Math.abs(determinant) < 1e-12) return null;

  const dx = (-uz.x * uy.y + uz.y * uy.x) / determinant;
  const dy = (-ux.x * uz.y + ux.y * uz.x) / determinant;
  const direction = { x: dx, y: dy, z: 1 };

  // The solve settles the line, not which end of it the reader is at. A face whose normal points
  // along the direction must be the one facing them, and that is a question the projector answers.
  return projector.isFrontFacing(direction, { x: 0, y: 0, z: 0 })
    ? direction
    : { x: -direction.x, y: -direction.y, z: -direction.z };
}

function lerp3(a: Vec3, b: Vec3, t: number): Vec3 {
  return { x: a.x + (b.x - a.x) * t, y: a.y + (b.y - a.y) * t, z: a.z + (b.z - a.z) * t };
}

/** Joins neighbouring pieces that agree, so a segment nothing cuts comes back as one piece. */
function merge(pieces: readonly Piece[]): readonly Piece[] {
  const out: Piece[] = [];
  for (const piece of pieces) {
    const last = out[out.length - 1];
    if (last?.hidden === piece.hidden) {
      out[out.length - 1] = { from: last.from, to: piece.to, hidden: last.hidden };
      continue;
    }
    out.push(piece);
  }
  return out;
}
